/**
 * KTP Alert Dialog Component
 * 
 * Shows an alert dialog when KTP validation issues are detected.
 */

import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertCircle, CreditCard } from "lucide-react";
import { useLocation } from "wouter";

export type KTPAlertType = "missing" | "invalid" | "not-validated";

interface KTPAlertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: KTPAlertType;
}

export function KTPAlertDialog({
  open,
  onOpenChange,
  type,
}: KTPAlertDialogProps) {
  const [, setLocation] = useLocation();

  const getAlertContent = () => {
    switch (type) {
      case "missing":
        return {
          title: "KTP Belum Diisi",
          description:
            "Nomor KTP Anda belum diisi. Silakan lengkapi KTP Anda di halaman profil untuk memastikan informasi yang akurat pada KTA.",
          actionText: "Lengkapi KTP",
        };
      case "invalid":
        return {
          title: "KTP Tidak Valid",
          description:
            "Nomor KTP Anda tidak valid. Silakan perbarui KTP Anda di halaman profil untuk memastikan informasi yang akurat.",
          actionText: "Perbarui KTP",
        };
      case "not-validated":
        return {
          title: "KTP Belum Divalidasi",
          description:
            "Nomor KTP Anda belum divalidasi. Silakan periksa dan perbarui KTP Anda di halaman profil untuk memastikan informasi yang akurat.",
          actionText: "Periksa KTP",
        };
    }
  };

  const content = getAlertContent();

  const handleGoToProfile = () => {
    onOpenChange(false);
    // Navigate to user profile with KTP tab
    setLocation("/user#ktp");
    // Ensure hash is set after navigation (in case of timing issues)
    setTimeout(() => {
      if (window.location.hash !== "#ktp") {
        window.location.hash = "ktp";
      }
    }, 100);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/20">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <AlertDialogTitle className="text-left">
              {content.title}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            <div className="space-y-2">
              <p>{content.description}</p>
              <div className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-3">
                <div className="flex items-start gap-2">
                  <CreditCard className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    <strong>Penting:</strong> Tanggal lahir anda akan tertulis di
                    KTA, kesalahan dalam penulisan nomer ktp akan mempengaruhi
                    data yang ditampilkan, dan bukan tanggung jawab dari aplikasi.
                  </p>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Nanti Saja</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleGoToProfile}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {content.actionText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

